import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Target, Calendar, Users, FileText, MessageSquare, Clock,
  CheckCircle2, AlertTriangle, Plus, Send, Loader2, ChevronRight,
  Edit3, Trash2, Upload, Eye, ThumbsUp, ThumbsDown, RotateCcw, Lock,
  Activity, Milestone, Package, Bell, UserPlus, Mail, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getHeaders = () => {
  const token = localStorage.getItem('d3_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-400' },
  in_progress: { label: 'In Progress', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', dot: 'bg-blue-400' },
  on_track: { label: 'On Track', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', dot: 'bg-emerald-400' },
  at_risk: { label: 'At Risk', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', dot: 'bg-amber-400' },
  blocked: { label: 'Blocked', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-400' },
  awaiting_client: { label: 'Awaiting Client', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', dot: 'bg-purple-400' },
  on_hold: { label: 'On Hold', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', dot: 'bg-orange-400' },
  completed: { label: 'Completed', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', dot: 'bg-emerald-400' },
  cancelled: { label: 'Cancelled', color: 'text-zinc-500', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-500' },
  pending: { label: 'Pending', color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-400' },
  awaiting_approval: { label: 'Awaiting Approval', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', dot: 'bg-purple-400' },
  approved: { label: 'Approved', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', dot: 'bg-emerald-400' },
  changes_requested: { label: 'Changes Requested', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', dot: 'bg-amber-400' },
  delayed: { label: 'Delayed', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-400' },
  draft: { label: 'Draft', color: 'text-zinc-400', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-400' },
  ready_for_review: { label: 'Ready for Review', color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20', dot: 'bg-blue-400' },
  awaiting_client_approval: { label: 'Awaiting Approval', color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', dot: 'bg-purple-400' },
  rejected: { label: 'Rejected', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-400' },
  superseded: { label: 'Superseded', color: 'text-zinc-500', bg: 'bg-zinc-500/10 border-zinc-500/20', dot: 'bg-zinc-500' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_started;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.bg} ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Target },
  { id: 'timeline', label: 'Timeline', icon: Activity },
  { id: 'milestones', label: 'Milestones', icon: Milestone },
  { id: 'deliverables', label: 'Deliverables', icon: Package },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'updates', label: 'Activity', icon: Clock },
];

// ── Overview Tab ──────────────────────────────────────────
const OverviewTab = ({ project, milestones, deliverables, onRefresh }) => {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const currentPhase = project.phases?.find(p => p.id === project.currentPhaseId);
  const pendingApprovals = deliverables.filter(d => d.status === 'awaiting_client_approval').length;
  const completedMilestones = milestones.filter(m => ['completed', 'approved'].includes(m.status)).length;
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', message: '' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [showOverride, setShowOverride] = useState(false);
  const [overrideVal, setOverrideVal] = useState(project.progressPercent || 0);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);

  const progressMode = project.progressMode || 'auto';

  const handleProgressOverride = async (e) => {
    e.preventDefault();
    if (!overrideReason.trim()) return toast.error('Reason is required');
    setOverrideLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}/progress/override`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ progressPercent: overrideVal, reason: overrideReason.trim() }),
      });
      if (res.ok) {
        toast.success(`Progress overridden to ${overrideVal}%`);
        setShowOverride(false);
        setOverrideReason('');
        onRefresh?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to override');
      }
    } catch { toast.error('Error'); }
    setOverrideLoading(false);
  };

  const switchToAuto = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}/progress/auto`, {
        method: 'POST', headers: getHeaders(),
      });
      if (res.ok) {
        toast.success('Switched to auto-calculated progress');
        onRefresh?.();
      }
    } catch { toast.error('Error'); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return;
    setInviteLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/invites`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ ...inviteForm, projectId: project.id }),
      });
      if (res.ok) {
        const inv = await res.json();
        const baseUrl = window.location.origin;
        setInviteLink(`${baseUrl}/invite?token=${inv.token}`);
        toast.success('Invitation created! Share the link with your client.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create invitation');
      }
    } catch { toast.error('Error'); }
    setInviteLoading(false);
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        // Fallback for non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = inviteLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Invite link copied!');
    } catch {
      toast.error('Failed to copy — please select and copy manually');
    }
  };

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid="progress-card">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Progress</p>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
              progressMode === 'manual' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/20' : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
            }`} data-testid="progress-mode-badge">{progressMode === 'manual' ? 'Manual' : 'Auto'}</span>
          </div>
          <p className="text-2xl font-bold text-white">{project.progressPercent || 0}%</p>
          <div className="h-1 bg-white/5 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${project.progressPercent || 0}%` }} />
          </div>
          {perms.canManageMilestones && (
            <div className="mt-2 flex gap-1">
              {progressMode === 'auto' ? (
                <button onClick={() => { setOverrideVal(project.progressPercent || 0); setShowOverride(true); }}
                  className="text-[10px] text-zinc-500 hover:text-indigo-400 transition-colors" data-testid="override-progress-btn">Override</button>
              ) : (
                <button onClick={switchToAuto}
                  className="text-[10px] text-amber-400 hover:text-emerald-400 transition-colors" data-testid="switch-auto-btn">Switch to Auto</button>
              )}
            </div>
          )}
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Current Phase</p>
          <p className="text-lg font-semibold text-white">{currentPhase?.name || 'Not started'}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Milestones</p>
          <p className="text-lg font-semibold text-white">{completedMilestones}/{milestones.length}</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Pending Approvals</p>
          <p className={`text-lg font-semibold ${pendingApprovals > 0 ? 'text-amber-400' : 'text-white'}`}>{pendingApprovals}</p>
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Project Details</h3>
          <dl className="space-y-3 text-sm">
            {project.description && <div><dt className="text-zinc-500 text-xs">Description</dt><dd className="text-zinc-300 mt-0.5">{project.description}</dd></div>}
            {project.clientName && <div><dt className="text-zinc-500 text-xs">Client</dt><dd className="text-zinc-300 mt-0.5">{project.clientName}</dd></div>}
            {project.serviceType && <div><dt className="text-zinc-500 text-xs">Service Type</dt><dd className="text-zinc-300 mt-0.5">{project.serviceType}</dd></div>}
            {project.templateName && <div><dt className="text-zinc-500 text-xs">Template</dt><dd className="text-indigo-400 mt-0.5 text-xs">{project.templateName}</dd></div>}
            {project.startDate && <div><dt className="text-zinc-500 text-xs">Start Date</dt><dd className="text-zinc-300 mt-0.5">{new Date(project.startDate).toLocaleDateString()}</dd></div>}
            {project.targetDate && <div><dt className="text-zinc-500 text-xs">Target Date</dt><dd className="text-zinc-300 mt-0.5">{new Date(project.targetDate).toLocaleDateString()}</dd></div>}
          </dl>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white">Team</h3>
            {perms.canEditProject && (
              <button onClick={() => setShowInvite(true)}
                className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg hover:bg-purple-500/20"
                data-testid="invite-client-btn"
              >
                <UserPlus className="w-3 h-3" /> Invite Client
              </button>
            )}
          </div>
          <div className="space-y-2">
            {(project.teamMembers || []).map(m => (
              <div key={m.userId} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-indigo-600/20 flex items-center justify-center text-[10px] font-bold text-indigo-400">
                  {m.name?.split(' ').map(n => n[0]).join('') || '?'}
                </div>
                <div>
                  <p className="text-xs text-white">{m.name}</p>
                  <p className="text-[10px] text-zinc-500 capitalize">{m.role}</p>
                </div>
              </div>
            ))}
            {(project.clientMembers || []).map(m => (
              <div key={m.userId} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-purple-600/20 flex items-center justify-center text-[10px] font-bold text-purple-400">
                  {m.name?.split(' ').map(n => n[0]).join('') || '?'}
                </div>
                <div>
                  <p className="text-xs text-white">{m.name}</p>
                  <p className="text-[10px] text-purple-400">Client</p>
                </div>
              </div>
            ))}
          </div>

          {/* Progress Override Modal */}
          {showOverride && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowOverride(false)}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="progress-override-modal">
                <h2 className="text-base font-semibold text-white mb-1">Override Progress</h2>
                <p className="text-xs text-zinc-500 mb-4">Set a manual progress value. This will override auto-calculation until you switch back.</p>
                <form onSubmit={handleProgressOverride} className="space-y-4">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-2">Progress: {overrideVal}%</label>
                    <input type="range" min="0" max="100" value={overrideVal} onChange={e => setOverrideVal(Number(e.target.value))}
                      className="w-full accent-indigo-500" data-testid="progress-slider" />
                    <div className="flex justify-between text-[10px] text-zinc-600 mt-1"><span>0%</span><span>50%</span><span>100%</span></div>
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1">Reason *</label>
                    <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                      placeholder="Why are you overriding the auto-calculated progress?"
                      className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40 resize-none" rows={2} required
                      data-testid="override-reason-input" />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setShowOverride(false)} className="px-4 py-2 text-sm text-zinc-400">Cancel</button>
                    <button type="submit" disabled={overrideLoading || !overrideReason.trim()}
                      className="px-5 py-2 text-sm font-medium bg-amber-600 hover:bg-amber-500 text-white rounded-lg disabled:opacity-40 flex items-center gap-2"
                      data-testid="submit-override-btn">
                      {overrideLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Set Progress
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}

          {/* Invite Client Modal */}
          {showInvite && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setShowInvite(false); setInviteLink(''); }}>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="invite-client-modal">
                <h2 className="text-base font-semibold text-white mb-1">Invite Client</h2>
                <p className="text-xs text-zinc-500 mb-4">Send an invitation to join this project</p>

                {!inviteLink ? (
                  <form onSubmit={handleInvite} className="space-y-3">
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Email *</label>
                      <input value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                        type="email" required placeholder="client@company.com"
                        className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
                        data-testid="invite-email-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Name</label>
                      <input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Client's name"
                        className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Personal Message</label>
                      <textarea value={inviteForm.message} onChange={e => setInviteForm(f => ({ ...f, message: e.target.value }))}
                        placeholder="Welcome aboard! Looking forward to collaborating..."
                        className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40 resize-none" rows={2} />
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm text-zinc-400">Cancel</button>
                      <button type="submit" disabled={inviteLoading}
                        className="px-5 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-500 text-white rounded-lg disabled:opacity-40 flex items-center gap-2"
                        data-testid="send-invite-btn">
                        {inviteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        Create Invitation
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 text-center">
                      <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                      <p className="text-sm text-emerald-300">Invitation Created!</p>
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Share this link with your client:</label>
                      <div className="flex gap-2">
                        <input value={inviteLink} readOnly onClick={(e) => e.target.select()}
                          className="flex-1 px-3 py-2.5 text-xs bg-white/5 border border-white/10 rounded-lg text-zinc-300 font-mono truncate" />
                        <button type="button" onClick={copyLink}
                          className="shrink-0 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white rounded-lg transition-colors cursor-pointer"
                          data-testid="copy-invite-link" title="Copy link">
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-600">Link expires in 72 hours</p>
                    <button onClick={() => { setShowInvite(false); setInviteLink(''); setInviteForm({ email: '', name: '', message: '' }); }}
                      className="w-full py-2 text-sm text-zinc-400 hover:text-white">Done</button>
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Timeline Tab ──────────────────────────────────────────
const TimelineTab = ({ project }) => {
  const phases = project.phases || [];
  const currentIdx = phases.findIndex(p => p.id === project.currentPhaseId);

  return (
    <div className="space-y-1">
      {phases.map((phase, idx) => {
        const isCurrent = phase.id === project.currentPhaseId;
        const isPast = idx < currentIdx;
        const isFuture = idx > currentIdx;

        return (
          <div key={phase.id} className="flex items-stretch gap-4" data-testid={`phase-${phase.id}`}>
            <div className="flex flex-col items-center w-8">
              <div className={`w-4 h-4 rounded-full border-2 mt-1 shrink-0 ${
                isPast ? 'bg-emerald-500 border-emerald-500' : isCurrent ? 'bg-indigo-500 border-indigo-500 ring-4 ring-indigo-500/20' : 'border-zinc-700 bg-transparent'
              }`}>
                {isPast && <CheckCircle2 className="w-3 h-3 text-white" style={{ margin: '-0.5px' }} />}
              </div>
              {idx < phases.length - 1 && <div className={`w-0.5 flex-1 my-1 ${isPast ? 'bg-emerald-500/40' : 'bg-zinc-800'}`} />}
            </div>
            <div className={`flex-1 pb-6 ${isFuture ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-3">
                <h4 className={`text-sm font-medium ${isCurrent ? 'text-indigo-300' : isPast ? 'text-emerald-300' : 'text-zinc-400'}`}>
                  {phase.name}
                </h4>
                {isCurrent && <span className="px-2 py-0.5 text-[10px] font-medium bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 rounded-full">Current</span>}
                {isPast && <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 rounded-full">Done</span>}
              </div>
              {(phase.startDate || phase.endDate) && (
                <p className="text-[11px] text-zinc-600 mt-0.5">
                  {phase.startDate && new Date(phase.startDate).toLocaleDateString()} {phase.endDate && `— ${new Date(phase.endDate).toLocaleDateString()}`}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Milestones Tab ────────────────────────────────────────
const MilestonesTab = ({ project, milestones, onRefresh }) => {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', dueDate: '' });
  const [loading, setLoading] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}/milestones`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Milestone added'); setShowAdd(false); setForm({ name: '', description: '', dueDate: '' }); onRefresh(); }
      else toast.error('Failed to add milestone');
    } catch { toast.error('Error'); }
    setLoading(false);
  };

  const updateStatus = async (msId, status) => {
    try {
      await fetch(`${API_URL}/api/projects/${project.id}/milestones/${msId}`, {
        method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ status }),
      });
      onRefresh();
      toast.success('Milestone updated');
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{milestones.length} Milestones</h3>
        {perms.canManageMilestones && (
          <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 rounded-lg hover:bg-indigo-600/30 transition-colors" data-testid="add-milestone-btn">
            <Plus className="w-3 h-3" /> Add Milestone
          </button>
        )}
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 space-y-3">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Milestone name" required
            className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40" data-testid="milestone-name-input" />
          <div className="flex gap-3">
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description"
              className="flex-1 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40" />
            <input type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              className="px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-indigo-500/40" />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-zinc-400">Cancel</button>
            <button type="submit" disabled={loading} className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg disabled:opacity-40" data-testid="save-milestone-btn">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {milestones.map(ms => (
          <div key={ms.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 flex items-center gap-4" data-testid={`milestone-${ms.id}`}>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
              ['completed', 'approved'].includes(ms.status) ? 'bg-emerald-500/10' : ms.status === 'in_progress' ? 'bg-blue-500/10' : 'bg-zinc-500/10'
            }`}>
              {['completed', 'approved'].includes(ms.status) ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Target className="w-4 h-4 text-zinc-400" />}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-white">{ms.name}</h4>
              {ms.description && <p className="text-xs text-zinc-500 mt-0.5 truncate">{ms.description}</p>}
              {ms.dueDate && <p className="text-[10px] text-zinc-600 mt-0.5">Due: {new Date(ms.dueDate).toLocaleDateString()}</p>}
            </div>
            <StatusBadge status={ms.status} />
            {perms.canManageMilestones && ms.status === 'pending' && (
              <button onClick={() => updateStatus(ms.id, 'in_progress')} className="text-[10px] text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-500/10">Start</button>
            )}
            {perms.canManageMilestones && ms.status === 'in_progress' && (
              <button onClick={() => updateStatus(ms.id, 'completed')} className="text-[10px] text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded bg-emerald-500/10" data-testid={`complete-milestone-${ms.id}`}>Complete</button>
            )}
          </div>
        ))}
        {milestones.length === 0 && <p className="text-center text-zinc-600 text-sm py-8">No milestones yet</p>}
      </div>
    </div>
  );
};

// ── Deliverables Tab ──────────────────────────────────────
const DeliverablesTab = ({ project, deliverables, onRefresh }) => {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const isClient = user?.role === 'client';
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [approvalModal, setApprovalModal] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}/deliverables`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify(form),
      });
      if (res.ok) { toast.success('Deliverable added'); setShowAdd(false); setForm({ name: '', description: '' }); onRefresh(); }
    } catch { toast.error('Error'); }
    setLoading(false);
  };

  const handleRelease = async (dlId) => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}/deliverables/${dlId}/release`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success('Released to client'); onRefresh(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{deliverables.length} Deliverables</h3>
        {perms.canCreateDeliverable && !isClient && (
          <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600/20 text-indigo-300 border border-indigo-500/30 rounded-lg hover:bg-indigo-600/30 transition-colors" data-testid="add-deliverable-btn">
            <Plus className="w-3 h-3" /> Add Deliverable
          </button>
        )}
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 space-y-3">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Deliverable name" required
            className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40" data-testid="deliverable-name-input" />
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description"
            className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-zinc-400">Cancel</button>
            <button type="submit" disabled={loading} className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg disabled:opacity-40" data-testid="save-deliverable-btn">Save</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {deliverables.map(dl => (
          <div key={dl.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4" data-testid={`deliverable-${dl.id}`}>
            <div className="flex items-center gap-3">
              <Package className="w-4 h-4 text-zinc-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium text-white">{dl.name}</h4>
                  <span className="text-[10px] text-zinc-600">v{dl.version}</span>
                </div>
                {dl.description && <p className="text-xs text-zinc-500 mt-0.5">{dl.description}</p>}
                {dl.fileName && <p className="text-[10px] text-indigo-400 mt-0.5 flex items-center gap-1"><FileText className="w-3 h-3" />{dl.fileName}</p>}
              </div>
              <StatusBadge status={dl.status} />
            </div>
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
              {!isClient && perms.canReleaseDeliverable && ['draft', 'ready_for_review', 'changes_requested'].includes(dl.status) && (
                <button onClick={() => handleRelease(dl.id)} className="text-[10px] px-2.5 py-1 rounded bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 flex items-center gap-1" data-testid={`release-${dl.id}`}>
                  <Eye className="w-3 h-3" /> Release to Client
                </button>
              )}
              {dl.status === 'awaiting_client_approval' && (isClient || user?.role === 'admin') && (
                <button onClick={() => setApprovalModal(dl)} className="text-[10px] px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 flex items-center gap-1" data-testid={`review-${dl.id}`}>
                  <ThumbsUp className="w-3 h-3" /> Review
                </button>
              )}
              {!isClient && ['approved', 'changes_requested', 'rejected'].includes(dl.status) && dl.status !== 'superseded' && (
                <button onClick={async () => {
                  const res = await fetch(`${API_URL}/api/projects/${project.id}/deliverables/${dl.id}/new-version`, { method: 'POST', headers: getHeaders() });
                  if (res.ok) { toast.success('New version created'); onRefresh(); }
                }} className="text-[10px] px-2.5 py-1 rounded bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 flex items-center gap-1">
                  <RotateCcw className="w-3 h-3" /> New Version
                </button>
              )}
            </div>
          </div>
        ))}
        {deliverables.length === 0 && <p className="text-center text-zinc-600 text-sm py-8">No deliverables yet</p>}
      </div>

      {/* Approval Modal */}
      {approvalModal && (
        <ApprovalModal
          deliverable={approvalModal}
          projectId={project.id}
          onClose={() => setApprovalModal(null)}
          onSubmitted={() => { setApprovalModal(null); onRefresh(); }}
        />
      )}
    </div>
  );
};

const ApprovalModal = ({ deliverable, projectId, onClose, onSubmitted }) => {
  const [decision, setDecision] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!decision) return;
    if (['changes_requested', 'rejected'].includes(decision) && !comment.trim()) {
      toast.error('Comment is required'); return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/deliverables/${deliverable.id}/approve`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ decision, comment }),
      });
      if (res.ok) { toast.success(`Deliverable ${decision.replace('_', ' ')}`); onSubmitted(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Error'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0d1221] border border-white/10 rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="approval-modal">
        <h2 className="text-base font-semibold text-white mb-1">Review: {deliverable.name}</h2>
        <p className="text-xs text-zinc-500 mb-4">v{deliverable.version}</p>
        <div className="flex gap-2 mb-4">
          {[
            { id: 'approved', label: 'Approve', icon: ThumbsUp, color: 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10' },
            { id: 'changes_requested', label: 'Request Changes', icon: Edit3, color: 'border-amber-500/30 text-amber-400 bg-amber-500/10' },
            { id: 'rejected', label: 'Reject', icon: ThumbsDown, color: 'border-red-500/30 text-red-400 bg-red-500/10' },
          ].map(opt => (
            <button key={opt.id} onClick={() => setDecision(opt.id)}
              className={`flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs font-medium transition-colors ${
                decision === opt.id ? opt.color : 'border-white/10 text-zinc-500 hover:border-white/20'
              }`} data-testid={`decision-${opt.id}`}>
              <opt.icon className="w-5 h-5" />
              {opt.label}
            </button>
          ))}
        </div>
        <textarea value={comment} onChange={e => setComment(e.target.value)}
          placeholder={decision === 'approved' ? 'Optional comment...' : 'Required: explain your feedback...'}
          className="w-full px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40 resize-none mb-4"
          rows={3} data-testid="approval-comment" />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400">Cancel</button>
          <button onClick={handleSubmit} disabled={!decision || loading}
            className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg disabled:opacity-40 transition-colors" data-testid="submit-approval-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Messages Tab ──────────────────────────────────────────
const MessagesTab = ({ project, messages, onRefresh }) => {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!content.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/projects/${project.id}/messages`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ content, isInternal }),
      });
      if (res.ok) { setContent(''); onRefresh(); }
    } catch { toast.error('Error'); }
    setLoading(false);
  };

  return (
    <div className="flex flex-col h-[500px]">
      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {messages.map(msg => (
          <div key={msg.id} className={`flex gap-3 ${msg.authorId === user?.id ? 'flex-row-reverse' : ''}`} data-testid={`msg-${msg.id}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-[10px] font-bold ${
              msg.isInternal ? 'bg-amber-500/20 text-amber-400' : msg.authorRole === 'client' ? 'bg-purple-600/20 text-purple-400' : 'bg-indigo-600/20 text-indigo-400'
            }`}>
              {msg.authorName?.split(' ').map(n => n[0]).join('') || '?'}
            </div>
            <div className={`max-w-[70%] ${msg.authorId === user?.id ? 'text-right' : ''}`}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[10px] text-zinc-500">{msg.authorName}</span>
                {msg.isInternal && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-0.5"><Lock className="w-2.5 h-2.5" /> Internal</span>}
                <span className="text-[10px] text-zinc-700">{new Date(msg.createdAt).toLocaleString()}</span>
              </div>
              <div className={`inline-block px-3 py-2 rounded-xl text-sm ${
                msg.isInternal ? 'bg-amber-500/5 border border-amber-500/10 text-amber-200'
                  : msg.authorId === user?.id ? 'bg-indigo-600/20 border border-indigo-500/20 text-zinc-200'
                  : 'bg-white/[0.03] border border-white/[0.06] text-zinc-300'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          </div>
        ))}
        {messages.length === 0 && <p className="text-center text-zinc-600 text-sm py-8">No messages yet</p>}
      </div>

      {/* Message input */}
      <div className="border-t border-white/5 pt-3">
        {perms.canSendInternalNotes && (
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => setIsInternal(false)}
              className={`text-[10px] px-2.5 py-1 rounded-lg border transition-colors ${!isInternal ? 'bg-indigo-600/10 border-indigo-500/30 text-indigo-300' : 'border-white/10 text-zinc-500 hover:text-zinc-300'}`} data-testid="msg-client-toggle">
              <MessageSquare className="w-3 h-3 inline mr-1" /> Client Message
            </button>
            <button onClick={() => setIsInternal(true)}
              className={`text-[10px] px-2.5 py-1 rounded-lg border transition-colors ${isInternal ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' : 'border-white/10 text-zinc-500 hover:text-zinc-300'}`} data-testid="msg-internal-toggle">
              <Lock className="w-3 h-3 inline mr-1" /> Internal Note
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <input value={content} onChange={e => setContent(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={isInternal ? 'Internal note (not visible to client)...' : 'Type a message...'}
            className="flex-1 px-3 py-2.5 text-sm bg-white/5 border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-indigo-500/40"
            data-testid="message-input"
          />
          <button onClick={handleSend} disabled={!content.trim() || loading}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl disabled:opacity-40 transition-colors"
            data-testid="send-message-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Activity/Updates Tab ──────────────────────────────────
const UpdatesTab = ({ updates }) => (
  <div className="space-y-3">
    {updates.map(u => (
      <div key={u.id} className="flex items-start gap-3" data-testid={`update-${u.id}`}>
        <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
          <Activity className="w-4 h-4 text-zinc-500" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-white">{u.title}</p>
          {u.description && <p className="text-xs text-zinc-500 mt-0.5">{u.description}</p>}
          <p className="text-[10px] text-zinc-700 mt-1">{new Date(u.createdAt).toLocaleString()}</p>
        </div>
      </div>
    ))}
    {updates.length === 0 && <p className="text-center text-zinc-600 text-sm py-8">No activity yet</p>}
  </div>
);

// ── Main Project Detail ───────────────────────────────────
export default function ProjectDetailScreen({ projectId, onBack }) {
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [messages, setMessages] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editStatus, setEditStatus] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const h = getHeaders();
      const [pRes, mRes, dRes, msgRes, uRes] = await Promise.all([
        fetch(`${API_URL}/api/projects/${projectId}`, { headers: h }),
        fetch(`${API_URL}/api/projects/${projectId}/milestones`, { headers: h }),
        fetch(`${API_URL}/api/projects/${projectId}/deliverables`, { headers: h }),
        fetch(`${API_URL}/api/projects/${projectId}/messages`, { headers: h }),
        fetch(`${API_URL}/api/projects/${projectId}/updates`, { headers: h }),
      ]);
      if (pRes.ok) setProject(await pRes.json());
      if (mRes.ok) setMilestones(await mRes.json());
      if (dRes.ok) setDeliverables(await dRes.json());
      if (msgRes.ok) setMessages(await msgRes.json());
      if (uRes.ok) setUpdates(await uRes.json());
    } catch { toast.error('Failed to load project'); }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStatusChange = async (newStatus) => {
    try {
      await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PATCH', headers: getHeaders(), body: JSON.stringify({ status: newStatus }),
      });
      fetchAll();
      setEditStatus(false);
      toast.success('Status updated');
    } catch { toast.error('Error'); }
  };

  if (loading || !project) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-indigo-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="project-detail-screen">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={onBack} className="mt-1 p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors" data-testid="back-to-projects">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-white">{project.name}</h1>
            <div className="relative">
              <button onClick={() => perms.canEditProject && setEditStatus(!editStatus)} data-testid="project-status-btn">
                <StatusBadge status={project.status} />
              </button>
              {editStatus && (
                <div className="absolute top-full left-0 mt-1 z-50 w-48 bg-[#111827] border border-white/10 rounded-lg shadow-xl py-1" data-testid="status-dropdown">
                  {['not_started', 'in_progress', 'on_track', 'at_risk', 'blocked', 'awaiting_client', 'on_hold', 'completed', 'cancelled'].map(s => (
                    <button key={s} onClick={() => handleStatusChange(s)}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-white/5 flex items-center gap-2">
                      <StatusBadge status={s} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500">
            {project.clientName && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {project.clientName}</span>}
            {project.serviceType && <span>{project.serviceType}</span>}
            {project.targetDate && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Due {new Date(project.targetDate).toLocaleDateString()}</span>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/5 pb-px overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'border-indigo-500 text-indigo-300' : 'border-transparent text-zinc-500 hover:text-zinc-300'
            }`} data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-3.5 h-3.5" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15 }}>
          {activeTab === 'overview' && <OverviewTab project={project} milestones={milestones} deliverables={deliverables} onRefresh={fetchAll} />}
          {activeTab === 'timeline' && <TimelineTab project={project} />}
          {activeTab === 'milestones' && <MilestonesTab project={project} milestones={milestones} onRefresh={fetchAll} />}
          {activeTab === 'deliverables' && <DeliverablesTab project={project} deliverables={deliverables} onRefresh={fetchAll} />}
          {activeTab === 'messages' && <MessagesTab project={project} messages={messages} onRefresh={fetchAll} />}
          {activeTab === 'updates' && <UpdatesTab updates={updates} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
